/**
 * Web twin of apps/mobile/components/FloatingFooter.js.
 *
 * White elevated card pinned above the safe-area, holding Back + one
 * primary CTA (Task Edit / onboarding hierarchy). Shared by capture +
 * execute surfaces.
 */
import type { ReactNode, ButtonHTMLAttributes } from 'react';
import { arrowBackOutline } from 'ionicons/icons';
import IonIcon from './IonIcon';

interface RootProps {
  children: ReactNode;
  className?: string;
  /**
   * Keep the card phone-width in the middle of a wide window — same
   * cluster as `MobileTabBar`. Capture / execute leave this off so
   * Back + CTA still pack to the start of a full-bleed card.
   */
  cluster?: boolean;
}

export function FloatingFooter({ children, className = '', cluster = false }: RootProps) {
  return (
    <div
      className={`pointer-events-none fixed inset-x-2 z-50 ${className}`}
      style={{ bottom: 'max(0.5rem, env(safe-area-inset-bottom))' }}
    >
      <div
        className={`pointer-events-auto flex items-center gap-2 rounded-[15px] bg-white px-2 py-2 shadow-[0_8px_28px_rgba(0,0,0,0.28)] ${
          cluster ? 'mx-auto max-w-md justify-between' : 'justify-start'
        }`}
      >
        {children}
      </div>
    </div>
  );
}

interface NavButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  /** Ionicons data-URL from `ionicons/icons`. Defaults to arrow-back. */
  icon?: string;
  /** Paint a filled ionicon (native list uses filled `list`). */
  iconFilled?: boolean;
}

export function FloatingFooterNavButton({
  label,
  icon = arrowBackOutline,
  iconFilled = false,
  className = '',
  ...rest
}: NavButtonProps) {
  return (
    <button
      type="button"
      className={`flex min-w-[3.5rem] shrink-0 flex-col items-center justify-center gap-0.5 rounded-xl px-1 py-1 text-x9-marine transition hover:bg-x9-marine/5 disabled:opacity-45 ${className}`}
      {...rest}
    >
      <span className="flex h-[30px] w-[42px] items-center justify-center" aria-hidden="true">
        <IonIcon icon={icon} size={20} filled={iconFilled} />
      </span>
      <span className="text-[11px] font-medium leading-none text-x9-marine">{label}</span>
    </button>
  );
}

interface CtaProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  /** `marine` = primaryDark fill (native Task Edit CTA). `danger` for deny. */
  tone?: 'marine' | 'danger' | 'muted';
}

export function FloatingFooterCta({
  children,
  tone = 'marine',
  className = '',
  ...rest
}: CtaProps) {
  const toneClass =
    tone === 'danger'
      ? 'bg-x9-error text-white hover:brightness-110'
      : tone === 'muted'
        ? 'bg-x9-marine/10 text-x9-marine hover:bg-x9-marine/15'
        : 'bg-x9-marine text-white hover:brightness-110';

  return (
    <button
      type="button"
      className={`inline-flex min-h-[48px] flex-[3] items-center justify-center gap-2 rounded-[10px] px-5 py-3 text-base font-semibold transition active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-45 ${toneClass} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export default FloatingFooter;
